/**
 * Shared property-image upload helpers.
 *
 * Previously the single-file uploader + the "upload this batch of new files"
 * loop were copy-pasted into property/create, property/edit, and
 * individual-property-request pages. This is the one source of truth.
 *
 * Pure of React — safe to unit-test and reuse from any page.
 */

import { resolveApiBaseUrl } from './apiClient';

const API_BASE_URL = resolveApiBaseUrl();

/**
 * Upload a single image file to `/images/upload`. Returns the stored URL
 * (Cloudinary/OBS `secure_url` or `url`), or `null` on any failure — callers
 * treat null as "abort this submit and ask the user to retry".
 */
export async function uploadImageToServer(file: File, token?: string): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/images/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      console.error('Image upload failed:', response.status, response.statusText);
      return null;
    }
    const data = await response.json();
    return data.secure_url || data.url || null;
  } catch (error) {
    console.error('Image upload error:', error);
    return null;
  }
}

export interface UploadedImages {
  /** Newly-uploaded image URLs, in input order. */
  uploadedUrls: string[];
  /** Subset of `uploadedUrls` whose source index was tagged as a floor plan. */
  floorPlanUrls: string[];
}

export interface UploadNewImagesOptions {
  /** Indices (into `files`) the user tagged as floor plans. */
  floorPlanIndices?: Set<number>;
  /** Called before each upload with (current, total) for progress UI. */
  onProgress?: (current: number, total: number) => void;
}

/**
 * Upload a batch of NEW image files in order, reporting progress. Returns the
 * uploaded URLs (+ the floor-plan subset), or `null` if ANY file failed — the
 * caller should then surface an error and abort the whole submit, exactly as the
 * three pages did inline before.
 */
export async function uploadNewImages(
  files: File[],
  token: string | undefined,
  { floorPlanIndices, onProgress }: UploadNewImagesOptions = {},
): Promise<UploadedImages | null> {
  const uploadedUrls: string[] = [];
  const floorPlanUrls: string[] = [];
  for (let i = 0; i < files.length; i++) {
    onProgress?.(i + 1, files.length);
    const url = await uploadImageToServer(files[i], token);
    if (!url) return null;
    uploadedUrls.push(url);
    if (floorPlanIndices?.has(i)) floorPlanUrls.push(url);
  }
  return { uploadedUrls, floorPlanUrls };
}
